import React, { useContext, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';

import { compose } from 'recompose';
import { withRouter, useParams } from 'react-router-dom';
import { Box, Text, CheckBox } from 'grommet';
import withLayout from '../../helpers/withLayout';
import axios from '../../helpers/axios';
import { BasicLoader } from '../../helpers/Loaders';
import { formatDownloadTitle } from '@helpers/utils/formatUtils';
import { useQuery } from '@helpers/utils/hooksUtils';
import { analytics } from '../../services/analytics';
import useZendeskWidget from '../../helpers/useZendeskWidget';

import { UserContext } from '../../services/Session';

import useEntitlements from '@hooks/monetization/useEntitlements';
import { usePaywall } from '../../components/Paywall/paywall.hooks';
import {
  ViewerEntitlementStatus,
  PaywallContext,
} from '../../components/Paywall/paywall.types';
import Paywall from '../../components/Paywall/Paywall';
import PaywallLock from '../../components/Paywall/PaywallLock';
import { shd } from '@shd/jslib/models';

import { HitScopeHeader, HitScopeFeedback } from './components/HitScopeHeader';
import ScoreByInning from './components/ScoreByInning';
import HitScopeClipList from './components/HitScopeCliplist';
import HitScopeDownloadButton from './components/HitScopeDownloadButton';
import HitScopeVideoPlayer from './components/HitScopeVideoPlayer';
import useHitScope from './useHitscope';
import { HitScopeClip } from './hitScope.types';

const GameHitScope: React.FC<RouteComponentProps> = ({ history }) => {
  const { gameid: gameId } = useParams<{ gameid: string }>();
  const query = useQuery();

  const [gameData, setGameData] = useState<
    (shd.TeamGame & shd.DnorGame) | null
  >();
  const [currentClip, setCurrentClip] = useState<HitScopeClip>();
  const [manualSelectedClip, setManualSelectedClip] = useState<
    HitScopeClip | undefined
  >();

  const [teamSelection, setTeamSelection] = useState<boolean[]>([
    query.get('us') === '1',
    query.get('them') === '1',
  ]);

  const { fetchClips, clips, m3u8Text, selectedClip, loading, error } =
    useHitScope({
      type: 'game',
    });

  const onClipChanged = (newClip: HitScopeClip) => {
    setCurrentClip(newClip);
  };

  useEffect(() => {
    setCurrentClip(selectedClip);
  }, [selectedClip]);

  useZendeskWidget();

  const userStore = useContext(UserContext);

  const {
    entitlementStatus: viewerEntitlementStatus,
    loading: entitledLoading,
  } = useEntitlements({
    teamId: gameData?.DNOR_teamId,
    sport: 'bbsb',
  });
  const { handleUnlockAccess } = usePaywall(PaywallContext.GAME_HITSCOPE);

  useEffect(() => {
    fetchClips({
      gameId,
      us: teamSelection[0] ? '1' : '0',
      them: teamSelection[1] ? '1' : '0',
    });
  }, [fetchClips, teamSelection, gameId]);

  const getGameData = () => {
    axios.get(`/api/team_game/${gameId}`).then((response) => {
      setGameData(response.data);
    });
  };

  useEffect(() => {
    analytics.track('HitScope View', {
      type: 'Game',
    });

    getGameData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!gameId) {
    return <BasicLoader size={200} fullPage />;
  }

  return (
    <>
      {entitledLoading ? (
        <BasicLoader />
      ) : (
        <>
          {viewerEntitlementStatus ===
            ViewerEntitlementStatus.NOT_ENTITLED_LOGGED_OUT && (
            <PaywallLock
              mode="gameHitscope"
              isLoggedIn={!!userStore.authUser}
              isPrivate={false}
            />
          )}
          {viewerEntitlementStatus ===
            ViewerEntitlementStatus.NOT_ENTITLED_LOGGED_IN && (
            <Paywall
              isOpen={
                viewerEntitlementStatus ===
                ViewerEntitlementStatus.NOT_ENTITLED_LOGGED_IN
              }
              context={PaywallContext.GAME_HITSCOPE}
              onClose={() => history.goBack()}
              onUnlockAccess={(term) => handleUnlockAccess(term)}
            />
          )}
          {viewerEntitlementStatus === ViewerEntitlementStatus.ENTITLED && (
            <Box gap="large" width="xlarge" margin="0 auto" pad="medium">
              <HitScopeHeader />
              {gameData && gameData.scoreGameState > 10 && (
                <ScoreByInning gameData={gameData} />
              )}
              {gameData && (
                <Box gap="small">
                  <Text>Show clips from...</Text>
                  <CheckBox
                    label={gameData.scoreOurName}
                    name="name"
                    value="0"
                    checked={teamSelection[0]}
                    onChange={(event) => {
                      query.set('us', event.target.checked ? '1' : '0');
                      history.push({ search: query.toString() });
                      setTeamSelection([
                        event.target.checked,
                        teamSelection[1],
                      ]);
                    }}
                  />
                  <CheckBox
                    label={gameData.J_opponentName}
                    name="name"
                    value="1"
                    checked={teamSelection[1]}
                    onChange={(event) => {
                      query.set('them', event.target.checked ? '1' : '0');
                      history.push({ search: query.toString() });
                      setTeamSelection([
                        teamSelection[0],
                        event.target.checked,
                      ]);
                    }}
                  />
                </Box>
              )}
              <HitScopeVideoPlayer
                clips={clips}
                m3u8Text={m3u8Text}
                onClipChanged={onClipChanged}
                seekToClip={manualSelectedClip}
              />
              <HitScopeDownloadButton
                numberOfClips={clips.length}
                title={formatDownloadTitle(
                  `hitscope-${gameData?.scoreOurName}-vs-${gameData?.J_opponentName}`
                )}
                type={'game'}
                downloadRequest={{
                  gameId,
                  us: teamSelection[0] ? '1' : '0',
                  them: teamSelection[1] ? '1' : '0',
                }}
              />
              <HitScopeFeedback />
              {clips && selectedClip && (
                <HitScopeClipList
                  track={{
                    type: 'game',
                    gameId,
                  }}
                  clips={clips}
                  setManualSelectedClip={setManualSelectedClip}
                  selectedClip={currentClip}
                  loading={loading}
                />
              )}
              {error && <Text color="red">{error}</Text>}
            </Box>
          )}
        </>
      )}
    </>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default compose<any, RouteComponentProps>(
  withLayout,
  withRouter
)(GameHitScope);
